import { Box } from '@mui/material'
import { RpcError } from '@protobuf-ts/runtime-rpc'
import { C1LoadingButton } from 'c1ui'
import { C1Icon } from 'components/core/icons/C1Icon'
import { RecommendationDenyTooltip } from 'components/core/insights'
import { TaskPrimaryActionsV2Variant } from 'components/core/task-list-v2/task-list-v2-components/TaskPrimaryActionsV2'
import { DenyIcon } from 'components/pages/task/action/button'
import { getDenyText } from 'components/pages/task/action/messages'
import { DenyWithCommentTaskDialog } from 'components/pages/task/details/task-content-v2/common/DenyWithCommentTaskDialog'
import { canActOn } from 'components/pages/task/state'
import { useAlert } from 'hooks/useAlert/index'
import { TaskValue } from 'lib/actions/task'
import { useSimpleModal } from 'lib/modals'
import { X } from 'lucide-react'
import { TaskActionType } from 'pbts/c1/api/task/v1/task'
import { FC, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { useTicketActionTracker } from 'redux_toolkit/lib/TicketActionTracker'

export const DenyPrimaryAction: FC<{ task: TaskValue, variant?: TaskPrimaryActionsV2Variant }> = ({ task, variant = 'default' }) => {
    const intl = useIntl()
    const { open } = useSimpleModal()
    const toast = useAlert()
    const ticketActionTracker = useTicketActionTracker()

    const onError = useCallback(
        (err: RpcError): void => {
            toast(
                intl.formatMessage(
                    {
                        id: 'task_deny_error',
                        defaultMessage: 'Error denying task. {code}: {msg}',
                    },
                    {
                        code: err.code,
                        msg: err.message,
                    },
                ),
                'error',
            )
        },
        [intl, toast],
    )

    const handleClick = useCallback(async () => {
        const instance = task.policy?.current?.instance
        const requiresComment =
            instance && instance.oneofKind === 'approval' && instance.approval.approval?.requireDenialReason
        if (requiresComment) {
            return open(DenyWithCommentTaskDialog, { task })()
        }
        try {
            await ticketActionTracker?.deny(task)
        } catch (err) {
            return onError(err as RpcError)
        }
    }, [onError, open, task, ticketActionTracker])

    if (!canActOn(task, TaskActionType.DENY)) {
        return null
    }

    return (
        <RecommendationDenyTooltip task={task}>
            <Box>
                <C1LoadingButton
                    startIcon={<DenyIcon task={task} />}
                    variant={variant === 'contained' ? 'contained' : 'outlined'}
                    key={`components_task_list_deny_option_${task.id}`}
                    onClick={handleClick}
                    loading={!ticketActionTracker}
                    color={variant === 'default' ? 'primary' : 'error'}
                    sx={variant === 'icon-button' ? { minWidth: 0 } : {}}
                    aria-label={variant === 'icon-button' ? getDenyText(intl, task) : undefined}
                >
                    {variant === 'icon-button' ? <C1Icon icon={X} /> : getDenyText(intl, task)}
                </C1LoadingButton>
            </Box>
        </RecommendationDenyTooltip>
    )
}
